import {
  AfterViewInit,
  OnDestroy,
  Component,
  ElementRef,
  Inject,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { takeReturnUrl } from '../../utils/return-url';
import { AuthService } from '../../services/auth.service';
import { CompanyGroupService } from '../../services/company-group.service';
import { GoogleAuthService } from '../../services/google-auth.service';
import { LoggerService } from '../../services/logger.service';
import { ReferralService, Referrer } from '../../services/referral.service';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideCheck,
  lucideCreditCard,
  lucideDot,
  lucideEye,
  lucideEyeOff,
  lucideGift,
  lucideLock,
  lucideShieldCheck,
  lucideTriangleAlert,
  lucideUsers,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmCheckboxImports } from '@spartan-ng/helm/checkbox';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { LogoComponent } from '../../components/logo/logo.component';
import { environment } from '../../../environments/environment';
import { captureAffiliateRef, clearAffiliateRef } from '../../utils/affiliate-ref';
import { formatRate } from '../../utils/currency.utils';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-register',
  standalone: true,
  templateUrl: './register.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCardImports,
    HlmCheckboxImports,
    HlmFieldImports,
    HlmInputImports,
    HlmInputGroupImports,
    HlmSeparatorImports,
    HlmSpinnerImports,
    HlmTypographyImports,
    LogoComponent,
  ],
  providers: [
    provideIcons({
      lucideCheck,
      lucideCreditCard,
      lucideDot,
      lucideEye,
      lucideEyeOff,
      lucideGift,
      lucideLock,
      lucideShieldCheck,
      lucideTriangleAlert,
      lucideUsers,
    }),
  ],
})
export class RegisterComponent implements OnInit, AfterViewInit, OnDestroy {
  registerForm: FormGroup;
  submitted = false;
  loading = false;
  private registrationConversionEmitted = false;
  error = '';
  showPassword = false;
  showConfirmPassword = false;
  passwordFocused = false;
  pendingGroupName: string | null = null;
  /** Token of a network's invite link (?verbund_invite=): registers a member account. */
  verbundInviteToken: string | null = null;
  inviteError: string | null = null;
  /**
   * Code of a register link (?ref=), or one remembered from an earlier visit: an
   * affiliate's or a recommending shop's (the backend tells them apart).
   */
  affiliateCode: string | null = null;
  /** The shop whose link this is, with the welcome offer; null for any other code. */
  referrer: Referrer | null = null;
  readonly formatRate = formatRate;
  redirectAfterRegister: string | null = null;
  googleAvailable = false;

  @ViewChild('googleBtn') googleBtn?: ElementRef<HTMLElement>;
  private disposeGoogleBtn: (() => void) | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private googleAuthService: GoogleAuthService,
    private router: Router,
    private route: ActivatedRoute,
    private logger: LoggerService,
    private groupService: CompanyGroupService,
    private referralService: ReferralService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    this.registerForm = this.formBuilder.group(
      {
        email: ['', [Validators.required, Validators.email]],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/),
          ],
        ],
        confirmPassword: ['', Validators.required],
        termsAccepted: [false, Validators.requiredTrue],
      },
      { validators: this.passwordsMatchValidator },
    );
  }

  ngOnInit(): void {
    this.redirectAfterRegister = this.route.snapshot.queryParamMap.get('redirect');
    this.pendingGroupName = this.route.snapshot.queryParamMap.get('groupName');
    if (isPlatformBrowser(this.platformId)) {
      this.affiliateCode = captureAffiliateRef(this.route.snapshot.queryParamMap.get('ref'));
    }

    this.verbundInviteToken = this.route.snapshot.queryParamMap.get('verbund_invite');
    // A network member takes no part in referrals, so its invite wins over a ref
    if (this.affiliateCode && !this.verbundInviteToken) {
      this.referralService.getReferrer(this.affiliateCode).subscribe({
        next: (referrer) => (this.referrer = referrer),
        // An affiliate's or unknown code: register as usual, without the banner
        error: () => (this.referrer = null),
      });
    }
    if (this.verbundInviteToken) {
      this.groupService.getPublicGroupInfo(this.verbundInviteToken).subscribe({
        next: (info) => (this.pendingGroupName = info.central_company_name || info.name),
        error: (err) =>
          (this.inviteError =
            err.error?.detail || 'Dieser Einladungslink ist ungültig oder abgelaufen.'),
      });
    }
  }

  async ngAfterViewInit() {
    if (!this.googleBtn) {
      return;
    }
    try {
      this.disposeGoogleBtn = await this.googleAuthService.renderButton(
        this.googleBtn.nativeElement,
        { text: 'signup_with' },
        (credential) => this.onGoogleCredential(credential),
      );
      this.googleAvailable = true;
    } catch (error) {
      // Blocked script or missing config must never break the regular registration
      this.logger.error('Google Sign-Up not available:', error);
    }
  }

  ngOnDestroy(): void {
    this.disposeGoogleBtn?.();
  }

  onGoogleCredential(credential: string) {
    if (this.loading) {
      return;
    }
    this.error = '';
    this.loading = true;

    lastValueFrom(
      this.authService.loginWithGoogle(
        credential,
        this.getRegistrationTrackingData(),
        'register',
        this.verbundInviteToken,
        this.affiliateCode,
      ),
    )
      .then((response) => {
        if (response.is_new_account) {
          this.emitRegistrationAdsConversion();
          clearAffiliateRef();
        }
        // No email verification needed with Google — skip /register-success
        // and mirror the login flow instead
        const redirect = takeReturnUrl(this.redirectAfterRegister);
        if (!redirect && !this.authService.isProfileCompleted()) {
          this.router.navigate(['/onboarding']);
        } else {
          this.router.navigateByUrl(redirect || '/');
        }
      })
      .catch((error) => {
        this.logger.error(error);
        // detail can be a structured object on some errors — only render strings
        this.error =
          (typeof error.error?.detail === 'string' ? error.error.detail : null) ||
          'Die Registrierung mit Google ist fehlgeschlagen. Bitte versuche es erneut.';
        this.loading = false;
      });
  }

  onPasswordFocus() {
    this.passwordFocused = true;
  }

  get f() {
    return this.registerForm.controls;
  }

  passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordsNotMatching: true };
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  readonly passwordRequirements = [
    { key: 'length', label: 'Mindestens 8 Zeichen' },
    { key: 'uppercase', label: 'Einen Großbuchstaben' },
    { key: 'lowercase', label: 'Einen Kleinbuchstaben' },
    { key: 'number', label: 'Eine Zahl' },
  ] as const;

  isPasswordRequirementMet(requirement: string): boolean {
    const password = this.registerForm.get('password')?.value || '';

    switch (requirement) {
      case 'length':
        return password.length >= 8;
      case 'uppercase':
        return /[A-Z]/.test(password);
      case 'lowercase':
        return /[a-z]/.test(password);
      case 'number':
        return /\d/.test(password);
      default:
        return false;
    }
  }

  get allPasswordRequirementsMet(): boolean {
    const password = this.registerForm.get('password')?.value || '';
    const hasMinLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);

    return hasMinLength && hasUppercase && hasLowercase && hasNumber;
  }

  onSubmit() {
    if (this.loading) {
      return;
    }

    this.submitted = true;
    this.error = '';

    if (this.registerForm.invalid) {
      return;
    }

    this.loading = true;

    this.authService
      .initialRegister({
        email: this.registerForm.value.email,
        password: this.registerForm.value.password,
        tracking_data: this.getRegistrationTrackingData(),
        verbund_invite_token: this.verbundInviteToken,
        affiliate_code: this.affiliateCode,
      })
      .subscribe({
        next: () => {
          this.emitRegistrationAdsConversion();
          clearAffiliateRef();
          const queryParams: Record<string, string> = {};
          if (this.redirectAfterRegister) queryParams['redirect'] = this.redirectAfterRegister;
          if (this.pendingGroupName) queryParams['groupName'] = this.pendingGroupName;
          this.router.navigate(['/register-success'], { queryParams });
        },
        error: (error) => {
          if (error.status === 400 && error.error?.detail?.errors) {
            // Process field-specific errors
            const fieldErrors = error.error.detail.errors;

            // Set field-specific errors on form controls
            fieldErrors.forEach((fieldError: { field: string; message: string }) => {
              const control = this.registerForm.get(this.mapFieldName(fieldError.field));
              if (control) {
                control.setErrors({ serverError: fieldError.message });
                control.markAsTouched(); // Mark as touched to trigger error display
              }
            });

            // Also show a general error message
            this.error = 'Bitte korrigiere die markierten Felder und versuche es erneut.';
          } else {
            this.error =
              error.error?.detail ||
              'Die Registrierung ist fehlgeschlagen. Bitte versuche es erneut.';
          }
          this.loading = false;
        },
      });
  }

  /**
   * Google Ads conversion via GA4 (event name must match the conversion in Google Ads).
   * Sent under the consent the visitor gave in the website's cookie banner (see index.html).
   * Skipped on local dev only (qa/prod send).
   */
  private emitRegistrationAdsConversion(): void {
    if (this.registrationConversionEmitted) {
      return;
    }
    if (!isPlatformBrowser(this.platformId) || environment.env === 'dev') {
      return;
    }
    if (typeof gtag !== 'function') {
      return;
    }
    gtag('event', 'ads_conversion_Registrieren_1');
    this.registrationConversionEmitted = true;
  }

  private getRegistrationTrackingData(): Record<string, string> | undefined {
    if (!isPlatformBrowser(this.platformId)) {
      return undefined;
    }
    return {
      page_url: window.location.href,
      referrer: document.referrer,
    };
  }

  /**
   * Maps API field names to form control names
   */

  private mapFieldName(apiFieldName: string): string {
    const fieldMapping: { [key: string]: string } = {
      email: 'email',
      password: 'password',
    };

    return fieldMapping[apiFieldName] || apiFieldName;
  }
}
